import { useState, useEffect, useCallback } from "react"
import { Container, Card, Row, Col, Spinner, Button, Form, Alert } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { Routes } from "../../routes"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { isSuperAdmin } from "../../common/helpers"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import IndicatorCard from "../../components/indicators/IndicatorCard"
import SalesFunnel from "../../components/indicators/SalesFunnel"
import CoverageGauge from "../../components/indicators/CoverageGauge"
import SuperAdminBackLink from "../../components/platform/SuperAdminBackLink"
import moment from "moment"
import { SalesFunnelItem, PlaceDistributionItem, VisitCoverage } from "../../types/Indicators.types"
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js"
import { Link } from "react-router-dom"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface IndicatorsPageProps {
  embedded?: boolean
  organizationId?: string
}

interface OrganizationOption {
  id: string
  name: string
}

export default function IndicatorsPage({ embedded = false, organizationId = "" }: IndicatorsPageProps) {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const canChooseOrganization = isSuperAdmin(user) && !organizationId

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)
  const [funnel, setFunnel] = useState<SalesFunnelItem[]>([])
  const [distribution, setDistribution] = useState<PlaceDistributionItem[]>([])
  const [coverage, setCoverage] = useState<VisitCoverage>({ totalPlaces: 0, visitedPlaces: 0, coveragePercent: 0 })
  const [compareStats, setCompareStats] = useState<any>(null)
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState(organizationId || userOrgId)
  const effectiveOrgId = organizationId || selectedOrgId || userOrgId

  const bindData = useCallback(async (startDate?: string, endDate?: string) => {
    if (!effectiveOrgId) return

    try {
      setIsLoading(true)
      const start = startDate || moment().startOf("month").format("YYYY-MM-DD")
      const end = endDate || moment().format("YYYY-MM-DD")

      const [funnelRes, distRes, coverageRes, compareRes] = await Promise.all([
        API.getAction("Indicators/SalesFunnel", `organizationId=${effectiveOrgId}`),
        API.getAction("Indicators/PlaceDistribution", `organizationId=${effectiveOrgId}&groupBy=city`),
        API.getAction("Indicators/VisitCoverage", `organizationId=${effectiveOrgId}&startDate=${start}&endDate=${end}`),
        API.getAction("Dashboard/CompareStats", `organizationId=${effectiveOrgId}&currentStartDate=${start}&currentEndDate=${end}`),
      ])

      if (funnelRes.data.status === "ok") setFunnel(funnelRes.data.response)
      if (distRes.data.status === "ok") setDistribution(distRes.data.response)
      if (coverageRes.data.status === "ok") setCoverage(coverageRes.data.response)
      if (compareRes.data.status === "ok") setCompareStats(compareRes.data.response)
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }, [effectiveOrgId])

  const loadOrganizations = useCallback(async () => {
    if (!canChooseOrganization) return

    try {
      setIsLoadingOrganizations(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status !== "ok") return

      const options = (res.data.response || []).map((item: any) => ({
        id: item.id,
        name: item.name,
      }))

      setOrganizations(options)
      if (!selectedOrgId && options.length > 0) {
        setSelectedOrgId(options[0].id)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingOrganizations(false)
    }
  }, [canChooseOrganization, selectedOrgId])

  useEffect(() => {
    if (canChooseOrganization) {
      loadOrganizations()
    }
  }, [canChooseOrganization, loadOrganizations])

  useEffect(() => {
    setSelectedOrgId(organizationId || userOrgId)
  }, [organizationId, userOrgId])

  useEffect(() => {
    if (effectiveOrgId) bindData()
  }, [effectiveOrgId, bindData])

  const distributionChart = {
    labels: distribution.slice(0, 15).map(d => d.group),
    datasets: [{
      label: t("Places"),
      data: distribution.slice(0, 15).map(d => d.count),
      backgroundColor: "#0d6efd",
      borderRadius: 4,
    }]
  }

  const content = (
    <>
      {!embedded && <SuperAdminBackLink />}

      {!embedded && (
        <h4 className="mb-3">
          <i className="fas fa-chart-line me-2 text-primary" />
          {t("Indicators")} - {t("Overview")}
        </h4>
      )}

      {embedded && (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h5 className="mb-0 fw-bold">
              <i className="fas fa-chart-line me-2 text-primary" />
              {t("Indicators")} - {t("Overview")}
            </h5>
            <small className="text-muted">{t("Operational summary for the selected organization")}</small>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {canChooseOrganization && (
              <Form.Select
                size="sm"
                value={selectedOrgId}
                onChange={e => setSelectedOrgId(e.target.value)}
                disabled={isLoadingOrganizations}
                style={{ minWidth: 240 }}
              >
                <option value="">{t("Select organization for indicators")}</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </Form.Select>
            )}
            <Button as={Link} to={Routes.IndicatorsSellers.path} variant="outline-secondary" size="sm">
              {t("By Sellers")}
            </Button>
            <Button as={Link} to={Routes.IndicatorsDispatch.path} variant="outline-secondary" size="sm">
              {t("Dispatch")}
            </Button>
            <Button as={Link} to={Routes.IndicatorsGps.path} variant="outline-secondary" size="sm">
              {t("GPS / Fleet")}
            </Button>
          </div>
        </div>
      )}

      {!embedded && canChooseOrganization && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="small text-muted">{t("Organization")}</span>
          <Form.Select
            size="sm"
            value={selectedOrgId}
            onChange={e => setSelectedOrgId(e.target.value)}
            disabled={isLoadingOrganizations}
            style={{ maxWidth: 320 }}
          >
            <option value="">{t("Select organization for indicators")}</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </Form.Select>
        </div>
      )}

      {!effectiveOrgId && (
        <Alert variant="warning" className="mb-3">
          {t("Select an organization to load indicators")}
        </Alert>
      )}

      <DateRangeFilter onFilter={(s, e) => bindData(s, e)} loading={isLoading} />

      {isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          {/* Compare stats cards */}
          {compareStats && (
            <Row className="g-3 mb-4">
              <Col xs={6} md={3}>
                <IndicatorCard
                  title={t("Visits (Current Period)")}
                  value={compareStats.currentPeriod?.visitsCount ?? 0}
                  previousValue={compareStats.previousPeriod?.visitsCount}
                  format="number"
                  icon="fa-walking"
                  color="#0d6efd"
                />
              </Col>
              <Col xs={6} md={3}>
                <IndicatorCard
                  title={t("Places (Current Period)")}
                  value={compareStats.currentPeriod?.placesCount ?? 0}
                  previousValue={compareStats.previousPeriod?.placesCount}
                  format="number"
                  icon="fa-map-marker-alt"
                  color="#198754"
                />
              </Col>
              <Col xs={6} md={3}>
                <IndicatorCard
                  title={t("Visit Coverage")}
                  value={coverage.coveragePercent}
                  format="percent"
                  icon="fa-bullseye"
                  color="#6f42c1"
                />
              </Col>
              <Col xs={6} md={3}>
                <IndicatorCard
                  title={t("Total Places")}
                  value={coverage.totalPlaces}
                  format="number"
                  icon="fa-building"
                  color="#fd7e14"
                />
              </Col>
            </Row>
          )}

          <Row className="g-3 mb-4">
            <Col md={4}>
              <SalesFunnel data={funnel} />
            </Col>
            <Col md={4}>
              <CoverageGauge data={coverage} />
            </Col>
            <Col md={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0">
                    <i className="fas fa-map me-2 text-primary" />
                    {t("Place Distribution")}
                  </h6>
                </Card.Header>
                <Card.Body>
                  {distribution.length > 0 ? (
                    <Bar
                      data={distributionChart}
                      options={{
                        responsive: true,
                        indexAxis: "y" as const,
                        plugins: { legend: { display: false } },
                        scales: { x: { beginAtZero: true } }
                      }}
                    />
                  ) : (
                    <div className="text-center text-muted py-4">{t("No data available")}</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  )

  if (embedded) {
    return content
  }

  return (
    <Container fluid className="px-4 py-3">
      {content}
    </Container>
  )
}
